import type { V1RemoteConnection } from '@/generated/hooks/bifrost/models';
import {
  Field,
  FieldContent,
  FieldLabel,
  FieldTitle,
  RadioGroup,
  RadioGroupItem,
  Text,
} from '@kognitos/lattice';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectionType } from '../schemas';

export interface IRemoteConnectionStepProps {
  remoteConnections: V1RemoteConnection[];
  connectionType: ConnectionType;
  selectedRemoteConnectionId?: string;
  onConnectionTypeChange: (type: ConnectionType) => void;
  onRemoteConnectionSelect: (id: string) => void;
  /** Whether to show the Public/Remote toggle. Defaults to true. */
  showConnectionTypeToggle?: boolean;
}

export function RemoteConnectionStep({
  remoteConnections,
  connectionType,
  selectedRemoteConnectionId,
  onConnectionTypeChange,
  onRemoteConnectionSelect,
  showConnectionTypeToggle = true,
}: IRemoteConnectionStepProps) {
  const { t: tIntegrations } = useTranslation('integrations');

  const handleConnectionTypeValueChange = useCallback(
    (value: string) => {
      onConnectionTypeChange(value as ConnectionType);
    },
    [onConnectionTypeChange],
  );

  return (
    <div className="space-y-6 w-full">
      {/* Type of Connection */}
      {showConnectionTypeToggle && (
        <div className="space-y-3">
          <Text weight="medium">
            {tIntegrations(
              'integrations.addConnection.remoteConnection.typeOfConnection',
            )}
          </Text>
          <RadioGroup
            value={connectionType}
            onValueChange={handleConnectionTypeValueChange}
            className="flex flex-row gap-2"
          >
            <FieldLabel
              htmlFor="connection-type-public"
              className={
                'cursor-pointer transition-colors duration-200 !rounded-lg flex-1'
              }
            >
              <Field
                orientation="horizontal"
                className="!p-3 !items-center gap-2"
              >
                <RadioGroupItem
                  value="public"
                  id="connection-type-public"
                  className="cursor-pointer"
                />
                <FieldContent>
                  <FieldTitle className="!font-normal !leading-none">
                    {tIntegrations(
                      'integrations.addConnection.remoteConnection.public',
                    )}
                  </FieldTitle>
                </FieldContent>
              </Field>
            </FieldLabel>

            <FieldLabel
              htmlFor="connection-type-remote"
              className="cursor-pointer transition-colors duration-200 !rounded-lg flex-1"
            >
              <Field
                orientation="horizontal"
                className="!p-3 !items-center gap-2"
              >
                <RadioGroupItem
                  value="remote"
                  id="connection-type-remote"
                  className="cursor-pointer"
                />
                <FieldContent>
                  <FieldTitle className="!font-normal !leading-none">
                    {tIntegrations(
                      'integrations.addConnection.remoteConnection.remote',
                    )}
                  </FieldTitle>
                </FieldContent>
              </Field>
            </FieldLabel>
          </RadioGroup>
        </div>
      )}

      {/* Select remote connection grid - only visible when "Remote" is selected or toggle is hidden */}
      {(connectionType === ConnectionType.Remote ||
        !showConnectionTypeToggle) && (
        <div className="space-y-2">
          <Text weight="medium">
            {tIntegrations(
              'integrations.addConnection.remoteConnection.selectRemoteConnection',
            )}
          </Text>
          <RadioGroup
            value={selectedRemoteConnectionId || ''}
            onValueChange={onRemoteConnectionSelect}
            className="grid grid-cols-2 gap-2"
          >
            {remoteConnections.map(rc => (
              <FieldLabel
                key={rc.id}
                htmlFor={`remote-conn-${rc.id}`}
                className="cursor-pointer transition-colors duration-200 !rounded-lg flex-1"
              >
                <Field
                  orientation="horizontal"
                  className="!p-3 !items-start gap-3"
                >
                  <RadioGroupItem
                    value={rc.id || ''}
                    id={`remote-conn-${rc.id}`}
                    className="cursor-pointer mt-0.5"
                  />
                  <FieldContent>
                    <FieldTitle className="!leading-none">
                      {rc.display_name}
                    </FieldTitle>
                    {rc.description && (
                      <Text
                        level="small"
                        className="text-muted-foreground line-clamp-2"
                      >
                        {rc.description}
                      </Text>
                    )}
                  </FieldContent>
                </Field>
              </FieldLabel>
            ))}
          </RadioGroup>
        </div>
      )}
    </div>
  );
}
